import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";
import atric from "../assets/atric-2.jpg";
import { FaArrowRight, FaCheckCircle, FaAward, FaUsers, FaShieldAlt, FaChartLine } from "react-icons/fa";
import { HiOutlineLightBulb, HiOutlineClipboardCheck, HiOutlineDocumentReport } from "react-icons/hi";

export default function HomeBanner2() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  const THEME = {
    primary: "#0f172a",
    accent: "#3b82f6", // Updated to a more vibrant corporate blue
    success: "#10b981",
    textMuted: "#64748b",
    bgLight: "#f8fafc",
    white: "#ffffff"
  };

  const processSteps = [
    {
      title: "PLAN",
      desc: "Strategic assessment of internal controls and risk management to ensure a focused audit approach",
      img: banner4,
      iconColor: "#6366f1",
      Icon: HiOutlineLightBulb
    },
    {
      title: "EXECUTE",
      desc: "Methodical application of ISA and professional standards to deliver high-quality results",
      img: banner2,
      iconColor: THEME.success,
      Icon: HiOutlineClipboardCheck
    },
    {
      title: "REPORT",
      desc: "Delivery of value-adding reports and management letters for strategic decision making",
      img: banner3,
      iconColor: "#f59e0b",
      Icon: HiOutlineDocumentReport
    }
  ];

  const highlights = [
    { title: "Expert Leadership", icon: <FaUsers />, text: "NBAA Certified" },
    { title: "Best Practices", icon: <FaShieldAlt />, text: "Global ISA Standards" },
    { title: "Excellence", icon: <FaChartLine />, text: "Value-Driven Results" }
  ];

  useEffect(() => {
    setReady(true);
  }, []);

  if (!ready) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <section className="hb2-main-wrapper">
      <div className="container">
        
        {/* ================= 01. PROCESS CARDS ================= */}
        <div className="row g-4 mb-5">
          {processSteps.map((step, idx) => (
            <div className="col-md-4" key={idx}>
              <div className="hb2-card h-100">
                <div className="hb2-card-img-container">
                  <img src={step.img} alt={step.title} className="hb2-card-img" />
                </div>
                <div className="hb2-card-body p-4 pt-5 text-center position-relative">
                  <div className="hb2-icon-badge" style={{ backgroundColor: step.iconColor }}>
                    <step.Icon size={24} color="white" />
                  </div>
                  <h5 className="hb2-card-title">{step.title}</h5>
                  <p className="hb2-card-text">{step.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ================= 02. STATS/HIGHLIGHTS BRIDGE ================= */}
        <div className="row g-3 mb-5 justify-content-center">
          {highlights.map((h, i) => (
            <div key={i} className="col-lg-3 col-md-6">
              <div className="hb2-mini-feature">
                <span className="hb2-mini-icon">{h.icon}</span>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{h.title}</h6>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>{h.text}</small>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* ================= 03. MAIN CONTENT SECTION ================= */}
        <div className="row align-items-center g-5">
          <div className="col-lg-6 order-2 order-lg-1">
            <div className="hb2-badge-pill mb-3">Professional Audit Firm</div>
            <h2 className="hb2-main-heading">
              A Higher Standard of <span className="text-highlight">Accountability</span>
            </h2>
            <p className="hb2-description mb-4">
              <strong>ATRIC Consultants</strong> is a premier audit firm registered with the <strong>NBAA (PF 407)</strong>. We specialize in transforming regulatory compliance into a catalyst for strategic business growth.
            </p>
            
            <div className="row g-3 mb-5">
               {["Independent Thinking", "Innovative Solutions", "Full Transparency", "Technical Competency"].map((item, i) => (
                 <div key={i} className="col-sm-6 d-flex align-items-center gap-2">
                   <FaCheckCircle className="hb2-check-icon" style={{ color: THEME.success }} />
                   <span className="hb2-list-item fw-medium">{item}</span>
                 </div>
               ))}
            </div>

            <button className="hb2-btn-primary" onClick={() => appContext.navTo({ item: "services" })}>
              View All Services <FaArrowRight />
            </button>
          </div>

          <div className="col-lg-6 order-1 order-lg-2">
             <div className="hb2-image-composition">
                <div className="hb2-image-accent-box"></div>
                <img src={atric} alt="ATRIC Team" className="hb2-main-img img-fluid shadow-lg" />
                <div className="hb2-experience-badge shadow-lg">
                  <div className="hb2-badge-icon-box">
                    <FaAward size={24} color="#fff" />
                  </div>
                  <div>
                    <div className="badge-title">15+ Years</div>
                    <div className="badge-subtitle">Expertise</div>
                  </div>
                </div>
             </div>
          </div>
        </div>
      </div>

      <style>{`
        .hb2-main-wrapper {
          background-color: ${THEME.bgLight};
          padding: 100px 0;
          font-family: 'Inter', sans-serif;
          color: ${THEME.primary};
        }

        .hb2-badge-pill {
          display: inline-block;
          background: rgba(59, 130, 246, 0.1);
          color: ${THEME.accent};
          padding: 6px 16px;
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .hb2-card {
          background: #fff;
          border: none;
          border-radius: 24px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          overflow: hidden;
        }

        .hb2-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }

        .hb2-card-img-container {
          height: 160px;
          width: 100%;
          overflow: hidden;
        }

        .hb2-card-img { 
          width: 100%; 
          height: 100%; 
          object-fit: cover; 
          transform: scale(1.01); /* Prevents jagged edges */
        }

        .hb2-icon-badge {
          position: absolute;
          top: -28px;
          left: 50%;
          transform: translateX(-50%);
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 4px solid #fff;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1);
        }

        .hb2-card-title {
          font-weight: 800;
          font-size: 1.1rem;
          margin-bottom: 12px;
          letter-spacing: 1px;
        }

        .hb2-card-text {
          font-size: 0.9rem;
          color: ${THEME.textMuted};
          line-height: 1.6;
        }

        .hb2-mini-feature {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #fff;
          padding: 12px 20px;
          border-radius: 16px;
          border: 1px solid rgba(0,0,0,0.04);
        }

        .hb2-mini-icon {
          color: ${THEME.accent};
          font-size: 1rem;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${THEME.bgLight};
          border-radius: 8px;
        }

        .hb2-main-heading {
          font-weight: 800;
          font-size: clamp(2.2rem, 4vw, 3rem);
          line-height: 1.1;
          margin-bottom: 25px;
        }

        .text-highlight { color: ${THEME.accent}; }

        .hb2-btn-primary {
          background: ${THEME.primary};
          color: #fff;
          border: none;
          padding: 18px 36px;
          border-radius: 12px;
          font-weight: 700;
          transition: 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 12px;
        }

        .hb2-btn-primary:hover {
          background: ${THEME.accent};
          transform: translateY(-2px);
        }

        .hb2-image-composition { 
          position: relative; 
          padding-right: 20px;
        }
        
        .hb2-image-accent-box {
          position: absolute;
          top: -15px;
          right: 0;
          width: 80%;
          height: 100%;
          background: ${THEME.accent};
          opacity: 0.05;
          border-radius: 40px;
          z-index: 0;
        }

        .hb2-main-img {
          border-radius: 40px;
          z-index: 1;
          position: relative;
          width: 100%;
          height: 450px;
          object-fit: cover;
        }

        .hb2-experience-badge {
          position: absolute;
          bottom: 30px;
          left: -20px;
          background: #fff;
          padding: 16px 24px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          z-index: 2;
        }

        .hb2-badge-icon-box {
          background: ${THEME.accent};
          padding: 10px;
          border-radius: 12px;
        }

        .badge-title {
          font-weight: 800;
          font-size: 1.2rem;
          color: ${THEME.primary};
          line-height: 1;
        }

        .badge-subtitle {
          font-size: 0.8rem;
          color: ${THEME.textMuted};
        }

        @media (max-width: 991px) {
          .hb2-main-wrapper { padding: 60px 0; }
          .hb2-main-img { height: 350px; }
          .hb2-experience-badge { left: 20px; bottom: -20px; }
        }
      `}</style>
    </section>
  );
}